import React, { useEffect, useState } from 'react';
import ChessPiece3DThree from './ChessPiece3DThree';

const Timeline = () => {
  const [scrollY, setScrollY] = useState(0);
  const [rocketPosition, setRocketPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const timelineItems = document.querySelectorAll('.timeline-item');
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      
      timelineItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8;
        if (isVisible) {
          item.classList.add('visible');
        }
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    const cards = document.querySelectorAll('.content-card');
    cards.forEach(card => {
      const handleMouseEnter = function() {
        this.style.transform = 'translateY(-10px) scale(1.02)';
      };
      const handleMouseLeave = function() {
        this.style.transform = 'translateY(0) scale(1)';
      };
      card.addEventListener('mouseenter', handleMouseEnter);
      card.addEventListener('mouseleave', handleMouseLeave);
      card._mouseEnterHandler = handleMouseEnter;
      card._mouseLeaveHandler = handleMouseLeave;
    });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cards.forEach(card => {
        if (card._mouseEnterHandler) {
          card.removeEventListener('mouseenter', card._mouseEnterHandler);
        }
        if (card._mouseLeaveHandler) {
          card.removeEventListener('mouseleave', card._mouseLeaveHandler);
        }
      });
    };
  }, []);

  // Handle rocket position changes for highlighting
  const handleRocketPositionChange = React.useCallback((position) => {
    // Use a requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      setRocketPosition(position);
      
      const timelineItems = document.querySelectorAll('.timeline-item');
      const highlightRange = {
        min: window.innerHeight * 0.4,
        max: window.innerHeight * 0.6
      };
      
      timelineItems.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.top + rect.height / 2;
        
        // Check if rocket is in highlight range and item is near rocket
        const isInHighlightRange = position >= highlightRange.min && position <= highlightRange.max;
        const isNearRocket = Math.abs(itemCenter - position) < 150; // 150px tolerance
        
        // Special case for last item - only highlight when rocket is very close
        const isLastItem = index === timelineItems.length - 1;
        const isLastItemNearRocket = isLastItem && Math.abs(itemCenter - position) < 100;
        
        if ((isInHighlightRange && isNearRocket) || (isLastItemNearRocket && position >= window.innerHeight * 0.5)) {
          item.classList.add('highlighted');
        } else {
          item.classList.remove('highlighted');
        }
      });
    });
  }, []); // Empty dependency array since we don't use any props or state

  const timelineData = [
    {
      date: "Aug 2025 - Present",
      title: "ASL Infrastructure Team",
      role: "Infrastructure Engineer",
      description: "Currently working on infrastructure solutions and system optimization.",
      techStack: ["Infrastructure", "DevOps", "System Administration"],
      isCurrent: true
    },
    {
      date: "Jan 2025 - July 2025",
      title: "Support Ticketing System with RAG",
      role: "Capstone Project",
      link: "https://chat.elvificent.com",
      description: "Developed an intelligent support ticketing system integrated with RAG (Retrieval-Augmented Generation) modal for automated response generation and ticket classification.",
      techStack: ["Spring Boot", "React", "RAG", "AI/ML"]
    },
    {
      date: "Dec 2024 - July 2025",
      title: "Travis Technology Group",
      role: "Software Developer",
      description: "Developed custom Odoo modules and managed server infrastructure. Implemented business-specific solutions and maintained production servers.",
      techStack: ["Odoo", "Python", "Server Management", "Linux"]
    },
    {
      date: "Jan 2024 - July 2024",
      title: "CTF Platform Development",
      role: "Personal Project",
      description: "Built a comprehensive Capture The Flag platform for cybersecurity competitions with real-time scoring, challenge management, and team collaboration features.",
      techStack: ["Web Development", "Security", "Database", "Real-time Systems"]
    },
    {
      date: "June 2023 - August 2023",
      title: "China Unicom",
      role: "IT Intern",
      description: "Developed automation solutions to streamline IT processes and improve operational efficiency using Spring Boot framework.",
      techStack: ["Spring Boot", "Automation", "Java", "Process Optimization"]
    }
  ];

  return (
    <div className="glass-pane">
      <ChessPiece3DThree
        scrollY={scrollY}
        timelineStartY={600} // adjust to match your timeline start (below scroll to explore)
        timelineEndY={2000}  // adjust to match your timeline end
        onRocketPositionChange={handleRocketPositionChange}
      />
      <main className="timeline-container">
        {timelineData.map((item, index) => (
          <div key={index} className="timeline-item" data-date={item.date.split(' ')[2]}>
            <div className="timeline-content">
              <div className="timeline-marker"></div>
              <div className={`content-card ${item.isCurrent ? 'current' : ''}`}>
                <span className="date">{item.date}</span>
                <h3>
                  {item.link ? (
                    <a 
                      href={item.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="project-link"
                    >
                      {item.title}
                    </a>
                  ) : (
                    item.title
                  )}
                </h3>
                <p className="role">{item.role}</p>
                <p className="description">{item.description}</p>
                <div className="tech-stack">
                  {item.techStack.map((tech, techIndex) => (
                    <span key={techIndex} className="tech">{tech}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
};

export default Timeline; 
